import type { Anomaly, Balance } from "../api";
import { Badge } from "../components";
import { absAmount, compareAmounts, formatTokens, subAmounts } from "../format";
import styles from "./Dashboard.module.css";

/**
 * Intitulé court par code d'anomalie.
 *
 * Le `message` du serveur dit ce qui ne va pas, mais il commence par des
 * chiffres : il faut un titre devant pour que l'œil comprenne en un coup ce
 * qu'il regarde. Un `Record` complet plutôt qu'une suite de `if` — le jour où
 * le contrat ajoute un code, c'est une erreur de compilation ici, pas une
 * bannière muette en démonstration.
 */
const TITRES: Record<Anomaly["code"], string> = {
  ledger_unbalanced: "Déséquilibre comptable",
  ledger_negative_bucket: "Seau négatif",
  ledger_inconsistent: "Grand livre incohérent",
};

export type DashboardAnomalieProps = {
  anomaly: Anomaly;
  balance: Balance;
};

/**
 * Avertissement d'incohérence comptable.
 *
 * **Visible, et non bloquant.** Les seaux restent affichés entiers au-dessous :
 * c'est une décision prise côté serveur (`reporting.WalletState`), et la
 * reprendre ici n'est pas une facilité. Masquer les chiffres au moment précis
 * où quelqu'un constate un déséquilibre le priverait de la seule information
 * dont il a besoin pour comprendre d'où il vient.
 *
 * D'où `role="status"` et non `role="alert"` : l'information est annoncée par
 * un lecteur d'écran quand il arrive dessus, sans interrompre ce qu'il lisait.
 */
export function DashboardAnomalie({ anomaly, balance }: DashboardAnomalieProps) {
  // L'écart entre les deux témoins du grand livre : la somme des quatre seaux
  // d'un côté, le total entré de l'autre. Arithmétique en `BigInt` via
  // `format/` — convertir ces montants en `number` réintroduirait exactement
  // l'arrondi silencieux que cette bannière sert à signaler.
  const ecart = subAmounts(balance.total, balance.in);
  const ecartAffichable = compareAmounts(ecart, "0") !== 0;
  const sens = compareAmounts(ecart, "0") > 0 ? "de trop" : "manquants";

  return (
    <aside className={styles.anomalie} role="status">
      <svg
        className={styles.anomalieIcone}
        viewBox="0 0 24 24"
        width="22"
        height="22"
        aria-hidden="true"
        fill="none"
        stroke="currentColor"
        strokeWidth={1.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M10.6 3.9 2.4 18.1A1.6 1.6 0 0 0 3.8 20.5h16.4a1.6 1.6 0 0 0 1.4-2.4L13.4 3.9a1.6 1.6 0 0 0-2.8 0Z" />
        <path d="M12 9v4.5" />
        <path d="M12 17h.01" />
      </svg>

      <div className={styles.anomalieTextes}>
        <p className={styles.anomalieTitre}>
          {TITRES[anomaly.code]}
          <Badge tone="warning" size="sm" dot>
            Anomalie détectée à la lecture
          </Badge>
        </p>

        {/* Le diagnostic est rédigé par le serveur et ne porte que des montants
            et des noms de seaux : il s'affiche tel quel, sans réécriture. */}
        <p className={styles.anomalieMessage}>{anomaly.message}</p>

        {ecartAffichable && (
          <p className={styles.anomalieMessage}>
            Somme des seaux {formatTokens(balance.total)} pour{" "}
            {formatTokens(balance.in)} entrés, soit{" "}
            <span className={styles.anomalieEcart}>
              {formatTokens(absAmount(ecart))} tokens {sens}
            </span>
            .
          </p>
        )}

        <p className={styles.anomalieRappel}>
          Les montants ci-dessous restent ceux du grand livre : ils ne sont ni
          rabotés, ni remis à zéro. Signalez cette anomalie avant toute
          facturation.
        </p>
      </div>
    </aside>
  );
}
